import React from 'react'
import { Vehicle, VehicleStatus } from '../types'
import { Clock, MapPin, Gauge, Battery, Signal } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

interface CVSVehicleListProps {
  vehicles: Vehicle[]
  selectedVehicle: Vehicle | null
  onVehicleSelect: (vehicle: Vehicle) => void
  loading: boolean
}

const CVSVehicleList: React.FC<CVSVehicleListProps> = ({
  vehicles,
  selectedVehicle,
  onVehicleSelect,
  loading
}) => {
  const getStatusColor = (status: VehicleStatus) => {
    switch (status) {
      case VehicleStatus.MOVING:
        return 'bg-green-500'
      case VehicleStatus.STOPPED:
        return 'bg-red-500'
      case VehicleStatus.IDLE:
        return 'bg-yellow-500'
      case VehicleStatus.OFFLINE:
        return 'bg-gray-500'
      default:
        return 'bg-gray-500'
    }
  }

  const getStatusText = (status: VehicleStatus) => {
    switch (status) {
      case VehicleStatus.MOVING:
        return 'En route'
      case VehicleStatus.STOPPED:
        return 'Arrêté'
      case VehicleStatus.IDLE:
        return 'Inactif'
      case VehicleStatus.OFFLINE:
        return 'Hors ligne'
      default:
        return 'Inconnu'
    }
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="h-full bg-white">
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-800">Tous ({vehicles.length})</h2>
          <div className="text-sm text-gray-600">
            {vehicles.filter(v => v.status === VehicleStatus.MOVING).length} en route
          </div>
        </div>
      </div>

      {/* Vehicle list */}
      <div className="overflow-y-auto" style={{ height: 'calc(100% - 60px)' }}>
        {vehicles.map((vehicle) => (
          <div
            key={vehicle.id}
            onClick={() => onVehicleSelect(vehicle)}
            className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
              selectedVehicle?.id === vehicle.id ? 'bg-blue-50 border-blue-200' : ''
            }`}
          >
            <div className="p-4">
              {/* Vehicle header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(vehicle.status)}`}></div>
                  <div>
                    <div className="font-medium text-gray-900">{vehicle.name}</div>
                    <div className="text-sm text-gray-500">
                      {vehicle.identifierType || 'ID'}: {vehicle.plateNumber}
                    </div>
                    {/* Show customer/container context */}
                    {vehicle.customerInfo?.name && (
                      <div className="text-xs text-blue-600">
                        👤 {vehicle.customerInfo.name}
                      </div>
                    )}
                    {vehicle.containerInfo?.name && (
                      <div className="text-xs text-green-600">
                        📦 {vehicle.containerInfo.name}
                      </div>
                    )}
                    {vehicle.assetInfo?.code && (
                      <div className="text-xs text-purple-600">
                        🚛 {vehicle.assetInfo.type}: {vehicle.assetInfo.code}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">{vehicle.speed} km/h</div>
                  <div className="text-xs text-gray-500">{getStatusText(vehicle.status)}</div>
                </div>
              </div>

              {/* Driver info */}
              {vehicle.driver && (
                <div className="text-sm text-gray-600 mb-2">
                  👤 {vehicle.driver.name}
                </div>
              )}

              {/* Location */}
              <div className="flex items-start space-x-2 mb-2">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-600 min-w-0">
                  <div className="truncate">
                    {vehicle.location.address || 'Adresse non disponible'}
                  </div>
                  <div className="text-xs text-gray-400">
                    {vehicle.location.latitude.toFixed(4)}, {vehicle.location.longitude.toFixed(4)}
                  </div>
                </div>
              </div>

              {/* Time and distance info */}
              <div className="grid grid-cols-2 gap-4 text-xs text-gray-500 mb-2">
                <div className="flex items-center space-x-1">
                  <Clock className="w-3 h-3" />
                  <span>Démarré: {formatTime(vehicle.lastUpdate)}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <span>Parcours auj.: {vehicle.odometer ? `${(vehicle.odometer / 1000).toFixed(1)} km` : '0 km'}</span>
                </div>
              </div>

              {/* Status indicators */}
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-3">
                  {vehicle.batteryLevel && (
                    <div className="flex items-center space-x-1">
                      <Battery className="w-3 h-3" />
                      <span>{vehicle.batteryLevel}%</span>
                    </div>
                  )}
                  {vehicle.signalStrength && (
                    <div className="flex items-center space-x-1">
                      <Signal className="w-3 h-3" />
                      <span>{vehicle.signalStrength}%</span>
                    </div>
                  )}
                  {vehicle.fuel && (
                    <div className="flex items-center space-x-1">
                      <Gauge className="w-3 h-3" />
                      <span>{vehicle.fuel}%</span>
                    </div>
                  )}
                </div>
                <div className="text-gray-400">
                  {formatDate(vehicle.lastUpdate)} {formatTime(vehicle.lastUpdate)}
                </div>
              </div>

              {/* Speed/distance summary for selected vehicle */}
              {selectedVehicle?.id === vehicle.id && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-lg font-semibold text-gray-900">
                        {vehicle.odometer ? (vehicle.odometer / 1000).toFixed(1) : '0.0'}
                      </div>
                      <div className="text-xs text-gray-500">km parcourus</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold text-gray-900">
                        {vehicle.engineHours || '0'}
                      </div>
                      <div className="text-xs text-gray-500">heures moteur</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold text-gray-900">
                        {vehicle.speed}
                      </div>
                      <div className="text-xs text-gray-500">km/h actuel</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {vehicles.length === 0 && (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <MapPin className="w-12 h-12 mb-2" />
          <p>Aucun véhicule disponible</p>
        </div>
      )}
    </div>
  )
}

export default CVSVehicleList
